'use client';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import AreaPriceChart from '@/components/shared/Charts/AreaPriceChart';
import { ChartTab } from '@/components/shared/Charts/ChartTab';
import TickerPriceDisplay from '@/components/shared/ChartSection/TickerPriceDisplay';
import TickerPriceDisplayPool from '@/components/shared/ChartSection/TickerPriceDisplayPool';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Section } from '@/components/shared/Section';
import { cn } from '@/lib/utils/cn';
import { SYMBOLS } from '@/ts/constants/C_CHART';
import {
  C_DEFAULT_POOL_TIMEFRAME,
  C_DEFAULT_PREDICTION_TIMEFRAME
} from '@/ts/constants/C_CHART_DEFAULTS';
import { T_Pool } from '@/ts/types/T_Pool';

import type { I_MarketData } from '@/server/actions/market/getHistory.action';
import type { T_Symbol, T_Timeframe } from '@/ts/types/T_Chart';

export interface I_ChartSectionProps {
  marketData?: I_MarketData;
  timeframe: T_Timeframe;
  symbol: T_Symbol; // Display symbol for ChartTab (pool.tokenSymbol)
  chartDataSymbol?: T_Symbol | string; // Symbol for WebSocket and chart data (prediction.shortName)
  title?: string; // Title for TickerPriceDisplay (prediction.predictionBase.title)
  url?: string;
  type?: 'prediction' | 'pool';
  variant?: 'default' | 'modal';
  setStateSymbol?: (stateSymbol: T_Symbol) => void;
  setStateTimeframe?: (timeframe: T_Timeframe) => void;
  poolSymbols?: string[];
  poolSlugs?: string[]; // Pool slugs for navigation
  currentSlug?: string; // Current pool slug
  poolDecimals?: number;
  pool?: T_Pool;
  disableSticky?: boolean;
  modalScrollContainerId?: string; // ID of scroll container for modal context
  children?: React.ReactNode;
  isLoading?: boolean;
}

export const ChartSection = memo(function ChartSection({
  marketData,
  timeframe,
  symbol,
  chartDataSymbol,
  title,
  url = '',
  type = 'prediction',
  variant = 'default',
  setStateSymbol,
  setStateTimeframe,
  poolSymbols,
  poolSlugs,
  currentSlug,
  poolDecimals,
  pool,
  disableSticky = false,
  modalScrollContainerId,
  children,
  isLoading = false
}: I_ChartSectionProps) {
  //! Use pool symbols (convert to T_Symbol[]) when poolSymbols provided, otherwise use SYMBOLS
  const tokens: T_Symbol[] = poolSymbols
    ? poolSymbols.map(sym => sym.toUpperCase() as T_Symbol)
    : SYMBOLS;

  //! Use chartDataSymbol for WebSocket and chart data, fallback to display symbol
  const dataSymbol = chartDataSymbol || symbol;

  const [isSticky, setIsSticky] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [tabHeight, setTabHeight] = useState(0);
  const isPrediction = type === 'prediction';
  const isStickyEnabled = !disableSticky;
  // For pools, use fixed positioning for page-wide sticky; for predictions, use CSS sticky
  const useFixedMode = isStickyEnabled && !isPrediction && variant !== 'modal';

  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLDivElement>(null);

  // Measure tab height for placeholder
  const measureTabHeight = useCallback(() => {
    if (tabRef.current) {
      setTabHeight(tabRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    measureTabHeight();
    window.addEventListener('resize', measureTabHeight);
    return () => window.removeEventListener('resize', measureTabHeight);
  }, [measureTabHeight]);

  useEffect(() => {
    if (!isStickyEnabled) return;

    // Determine scroll container based on context
    const scrollContainer = modalScrollContainerId
      ? document.getElementById(modalScrollContainerId)
      : null;

    // Adjust rootMargin based on variant
    // Modal: 0px - detect when sentinel scrolls out of modal viewport
    // Page: -62px - account for main header height
    const rootMargin =
      variant === 'modal' ? '0px 0px 0px 0px' : '-62px 0px 0px 0px';

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.target === topSentinelRef.current) {
            setIsSticky(!entry.isIntersecting);
          } else if (entry.target === bottomSentinelRef.current) {
            // Only hide sticky for prediction page variant (not modal or pool)
            // For pools, we want sticky to persist throughout entire page scroll
            if (variant !== 'modal' && isPrediction) {
              setIsHidden(
                !entry.isIntersecting && entry.boundingClientRect.top < 100
              );
            }
          }
        });
      },
      {
        root: scrollContainer, // Use modal scroll container when provided
        threshold: 0,
        rootMargin
      }
    );

    const topSentinel = topSentinelRef.current;
    const bottomSentinel = bottomSentinelRef.current;

    if (topSentinel) {
      observer.observe(topSentinel);
    }
    if (bottomSentinel) {
      observer.observe(bottomSentinel);
    }

    return () => {
      observer.disconnect();
    };
  }, [isStickyEnabled, modalScrollContainerId, variant, isPrediction]);

  // For pool fixed mode: when isSticky, use fixed positioning
  // For predictions: use CSS sticky as before
  const stickyClasses = isStickyEnabled
    ? useFixedMode
      ? cn(
          isSticky
            ? `fixed left-0 right-0 z-50 bg-background border-b border-border/50 shadow-sm transition-all duration-200 ${url?.includes('/profile/') ? 'top-17 md:top-15' : 'top-15'}`
            : 'relative'
        )
      : cn(
          'sticky z-11 bg-background border-b border-border/50 shadow-sm transition-all duration-200',
          variant === 'modal' ? 'top-0 w-full' : 'top-15',
          isHidden && ' opacity-0 pointer-events-none'
        )
    : 'relative';

  return (
    <Section className="w-full py-0 md:py-0">
      {isStickyEnabled && <div ref={topSentinelRef} className="h-0.5 w-full" />}
      {/* Placeholder to prevent layout shift when tabs switch to fixed */}
      {useFixedMode && isSticky && <div style={{ height: tabHeight }} />}
      {useFixedMode ? (
        <>
          {/* Pool fixed mode: only ChartTab is fixed, price ticker stays in normal flow */}
          <div ref={tabRef} className={stickyClasses}>
            <ChartTab
              variant={variant}
              setStateSymbol={setStateSymbol}
              url={url}
              tokens={tokens}
              timeframe={timeframe}
              selectedToken={symbol}
              type={type}
              poolSlugs={poolSlugs}
              currentSlug={currentSlug}
              poolSymbols={poolSymbols}
            />
          </div>
          {!isLoading && marketData && poolDecimals ? (
            <TickerPriceDisplayPool
              marketData={marketData}
              symbol={dataSymbol}
              decimals={poolDecimals}
              pool={pool}
              isSticky={false}
            />
          ) : null}
        </>
      ) : (
        <div ref={tabRef} className={stickyClasses}>
          {!(type === 'pool' && variant === 'modal') && (
            <ChartTab
              variant={variant}
              setStateSymbol={setStateSymbol}
              url={url}
              tokens={tokens}
              timeframe={timeframe}
              selectedToken={symbol}
              type={type}
              poolSlugs={poolSlugs}
              currentSlug={currentSlug}
              poolSymbols={poolSymbols}
            />
          )}
          {isLoading
            ? null
            : marketData && (
                <>
                  {isPrediction ? (
                    <TickerPriceDisplay
                      marketData={marketData}
                      symbol={dataSymbol}
                      title={title}
                      isSticky={isSticky}
                    />
                  ) : poolDecimals ? (
                    <TickerPriceDisplayPool
                      marketData={marketData}
                      symbol={dataSymbol}
                      decimals={poolDecimals}
                      pool={pool}
                      isSticky={false} // Always false for pools
                    />
                  ) : null}
                </>
              )}
        </div>
      )}
      {isLoading ? (
        <div className="w-full min-h-210 flex items-center justify-center">
          <LoadingSpinner size={40} />
        </div>
      ) : (
        <>
          {marketData && (
            <AreaPriceChart
              symbol={dataSymbol as T_Symbol}
              type={type}
              initialTimeframe={
                type === 'pool'
                  ? C_DEFAULT_POOL_TIMEFRAME
                  : C_DEFAULT_PREDICTION_TIMEFRAME
              }
              initialData={marketData.history}
              variant={variant}
              timeframe={timeframe}
              setStateTimeframe={setStateTimeframe}
            />
          )}
          <div className={cn('relative', isPrediction && 'z-0')}>
            {children}
          </div>
        </>
      )}
    </Section>
  );
});

export default ChartSection;
